import { Card, isJoker, isWild } from '../utils/CardUtils';
import { Suit } from '../utils/Constants';

export type MeldType = 'set' | 'run';

export interface Meld {
  id: string;
  type: MeldType;
  cards: Card[];
  playerId: string;
}

let meldCounter = 0;
export function nextMeldId(): string {
  return `meld_${++meldCounter}`;
}
export function resetMeldCounter(): void {
  meldCounter = 0;
}

export function validateSet(cards: Card[]): boolean {
  if (cards.length < 3) return false;
  if (cards.length > 9) return false;

  const naturals = cards.filter(c => !isWild(c));
  const wilds = cards.filter(c => isWild(c));

  if (wilds.length > 1) return false;
  if (naturals.length === 0) return false;

  // All naturals must have the same rank
  const rank = naturals[0].rank;
  if (!naturals.every(c => c.rank === rank)) return false;

  // No more than one card from each suit (across both decks, so max 8 naturals)
  // Actually in Buraco with 2 decks, you can have 2 of same suit (one from each deck)
  return true;
}

export function validateRun(cards: Card[]): boolean {
  if (cards.length < 3) return false;

  // Determine the suit from natural (non-wild) cards
  const naturals = cards.filter(c => !isWild(c));
  if (naturals.length === 0) return false;

  const suit = naturals[0].suit;
  if (!naturals.every(c => c.suit === suit)) return false;

  // Count wilds — check if any 2 is natural (in its correct suit position)
  let wildCount = 0;
  const resolved = resolveRun(cards, suit);
  if (!resolved) return false;

  // Check wild count in resolved run
  for (let i = 0; i < resolved.length; i++) {
    const card = cards[i];
    const expectedRank = resolved[i];
    if (isJoker(card)) {
      wildCount++;
    } else if (card.rank === 2 && expectedRank !== 2) {
      wildCount++;
    }
  }

  return wildCount <= 1;
}

// Resolve a run to determine the rank sequence, returns null if invalid
export function resolveRun(cards: Card[], suit: Suit): number[] | null {
  // Get fixed positions: natural cards that aren't wild
  const positions: { index: number; rank: number; isFixed: boolean }[] = [];

  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    if (isJoker(card)) {
      positions.push({ index: i, rank: 0, isFixed: false });
    } else if (card.rank === 2 && card.suit !== suit) {
      // 2 of different suit is always wild
      positions.push({ index: i, rank: 0, isFixed: false });
    } else {
      positions.push({ index: i, rank: card.rank, isFixed: true });
    }
  }

  // Find a fixed card to anchor the sequence
  const fixed = positions.filter(p => p.isFixed);
  if (fixed.length === 0) return null;

  // Try to build sequence from the first fixed card
  const anchor = fixed[0];
  const startRank = anchor.rank - anchor.index;

  // Check if sequence is valid (ranks 1-13, with ace-low or ace-high)
  const result: number[] = [];
  for (let i = 0; i < cards.length; i++) {
    const rank = startRank + i;
    if (rank < 1 || rank > 13) {
      // Try ace-high: if startRank would be negative, ace is 14
      return tryAceHighRun(cards, positions, suit);
    }
    result.push(rank);
  }

  // Verify all fixed cards match
  for (const p of fixed) {
    if (result[p.index] !== p.rank) {
      return tryAceHighRun(cards, positions, suit);
    }
  }

  return result;
}

function tryAceHighRun(cards: Card[], positions: { index: number; rank: number; isFixed: boolean }[], suit: Suit): number[] | null {
  const fixed = positions.filter(p => p.isFixed);
  if (fixed.length === 0) return null;

  // For ace-high, treat ace as 14
  const adjustedFixed = fixed.map(p => ({
    ...p,
    rank: p.rank === 1 ? 14 : p.rank,
  }));

  const anchor = adjustedFixed[0];
  const startRank = anchor.rank - anchor.index;

  const result: number[] = [];
  for (let i = 0; i < cards.length; i++) {
    const rank = startRank + i;
    if (rank < 1 || rank > 14) return null;
    result.push(rank === 14 ? 1 : rank);
  }

  for (const p of adjustedFixed) {
    const expected = result[p.index] === 1 ? 14 : result[p.index];
    if (expected !== p.rank) return null;
  }

  return result;
}

export function validateMeld(cards: Card[]): MeldType | null {
  if (validateSet(cards)) return 'set';
  if (validateRun(cards)) return 'run';
  return null;
}

export function isBurraco(meld: Meld): boolean {
  return meld.cards.length >= 7;
}

export function isCleanBurraco(meld: Meld): boolean {
  if (!isBurraco(meld)) return false;
  if (meld.type === 'set') {
    return meld.cards.every(c => !isWild(c));
  }
  // For runs, a 2 in its natural position is clean
  const suit = meld.cards.find(c => !isJoker(c) && !(c.rank === 2 && c.suit !== meld.cards.find(cc => !isWild(cc))?.suit))?.suit;
  if (!suit) return false;
  const resolved = resolveRun(meld.cards, suit);
  if (!resolved) return false;

  for (let i = 0; i < meld.cards.length; i++) {
    const card = meld.cards[i];
    if (isJoker(card)) return false;
    if (card.rank === 2 && resolved[i] !== 2) return false;
  }
  return true;
}

export function canAddToMeld(meld: Meld, card: Card): boolean {
  // Try adding the card at each valid position
  if (meld.type === 'set') {
    const testCards = [...meld.cards, card];
    return validateSet(testCards);
  } else {
    // For runs, try prepending or appending
    const prepend = [card, ...meld.cards];
    const append = [...meld.cards, card];
    return validateRun(prepend) || validateRun(append);
  }
}

export function addToMeld(meld: Meld, card: Card): Meld | null {
  if (meld.type === 'set') {
    const testCards = [...meld.cards, card];
    if (validateSet(testCards)) {
      return { ...meld, cards: testCards };
    }
    return null;
  }

  // For runs, try appending first then prepending
  const append = [...meld.cards, card];
  if (validateRun(append)) {
    return { ...meld, cards: append };
  }
  const prepend = [card, ...meld.cards];
  if (validateRun(prepend)) {
    return { ...meld, cards: prepend };
  }
  return null;
}
